import React, { useState, useEffect } from 'react';
import { useObras } from '../../context/ObrasContextSupabase';
import { BarChart2, TrendingUp, AlertTriangle, Calendar, DollarSign, Percent, FileDown, Info } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

const RelatorioGerencial = ({ obraId }) => {
  const { obterObraPorId } = useObras();
  const [obra, setObra] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [projecoes, setProjecoes] = useState(null);
  const [medicoesAcumuladas, setMedicoesAcumuladas] = useState([]);
  const [dadosCurvaS, setDadosCurvaS] = useState([]);

  useEffect(() => {
    if (obraId) {
      const obraData = obterObraPorId(obraId);
      setObra(obraData);
      
      if (obraData) {
        // Verificar se a obra tem itens antes de chamar as funções que dependem deles
        if (obraData.itens && obraData.itens.length > 0) {
          gerarAlertas(obraData);
          calcularProjecoes(obraData);
          calcularMedicoesAcumuladas(obraData);
          calcularDadosCurvaS(obraData);
        } else {
          // Limpar estados para evitar erros
          setAlertas([]);
          setProjecoes(null);
          setMedicoesAcumuladas([]);
          setDadosCurvaS([]);
        }
      }
    }
  }, [obraId, obterObraPorId]);

  // Função para formatar valor monetário
  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  // Função para formatar percentual
  const formatarPercentual = (valor) => {
    return `${valor.toFixed(2)}%`;
  };

  // Função para formatar data
  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  // Gerar alertas baseados nos dados da obra
  const gerarAlertas = (obra) => {
    if (!obra || !obra.itens || obra.itens.length === 0) {
      setAlertas([]);
      return;
    }

    const listaAlertas = [];

    // Verificar prazo
    const hoje = new Date();
    const dataFim = new Date(obra.dataPrevisaoTermino);
    const diasRestantes = Math.ceil((dataFim - hoje) / (1000 * 60 * 60 * 24));
    
    // Alerta de prazo
    if (diasRestantes < 0) {
      listaAlertas.push({
        tipo: 'erro',
        mensagem: `Obra atrasada em ${Math.abs(diasRestantes)} dias.`,
        icone: <Calendar size={18} />
      });
    } else if (diasRestantes <= 30) {
      listaAlertas.push({
        tipo: 'aviso',
        mensagem: `Prazo próximo do fim: ${diasRestantes} dias restantes.`,
        icone: <Calendar size={18} />
      });
    }

    // Verificar progresso vs tempo decorrido
    if (obra.itens && obra.itens.length > 0) {
      const dataInicio = new Date(obra.dataInicio);
      const tempoTotal = dataFim - dataInicio;
      const tempoDecorrido = hoje - dataInicio;
      const percentualTempoDecorrido = (tempoDecorrido / tempoTotal) * 100;
      
      // Calcular progresso médio
      const progressoMedio = obra.itens.reduce((acc, item) => acc + (item.progresso || 0), 0) / obra.itens.length;
      
      // Diferença entre tempo decorrido e progresso
      const diferenca = percentualTempoDecorrido - progressoMedio;
      
      if (diferenca > 20) {
        listaAlertas.push({
          tipo: 'erro',
          mensagem: `Progresso atrasado: ${percentualTempoDecorrido.toFixed(1)}% do tempo vs ${progressoMedio.toFixed(1)}% do trabalho.`,
          icone: <Percent size={18} />
        });
      } else if (diferenca > 10) {
        listaAlertas.push({
          tipo: 'aviso',
          mensagem: `Progresso ligeiramente atrasado: ${percentualTempoDecorrido.toFixed(1)}% do tempo vs ${progressoMedio.toFixed(1)}% do trabalho.`,
          icone: <Percent size={18} />
        });
      }
    }

    // Verificar desvios orçamentários
    if (obra.itens && obra.itens.length > 0) {
      const valorTotalPrevisto = obra.itens.reduce((total, item) => total + item.total, 0);
      const valorTotalMedido = obra.itens.reduce((total, item) => total + (item.totalMedido || 0), 0);
      
      // Verificar se o valor medido está próximo do valor da obra
      if (valorTotalMedido > parseFloat(obra.valor) * 0.9) {
        listaAlertas.push({
          tipo: 'erro',
          mensagem: `Valor medido (${formatarMoeda(valorTotalMedido)}) próximo do limite contratual (${formatarMoeda(obra.valor)}).`,
          icone: <DollarSign size={18} />
        });
      }
      
      // Verificar itens com medição acima do previsto
      const itensComExcesso = obra.itens.filter(item => (item.quantidadeMedida || 0) > item.quantidade);
      if (itensComExcesso.length > 0) {
        listaAlertas.push({
          tipo: 'aviso',
          mensagem: `${itensComExcesso.length} item(ns) com quantidade medida acima do previsto.`,
          icone: <AlertTriangle size={18} />
        });
      }
    }

    setAlertas(listaAlertas);
  };

  // Calcular projeções
  const calcularProjecoes = (obra) => {
    if (!obra || !obra.itens || obra.itens.length === 0) {
      setProjecoes(null);
      return;
    }

    // Calcular valor total previsto e medido
    const valorTotalPrevisto = obra.itens.reduce((total, item) => total + item.total, 0);
    const valorTotalMedido = obra.itens.reduce((total, item) => total + (item.totalMedido || 0), 0);
    
    // Calcular progresso médio
    const progressoMedio = obra.itens.reduce((acc, item) => acc + (item.progresso || 0), 0) / obra.itens.length;
    
    // Calcular data de início e prazo
    const dataInicio = new Date(obra.dataInicio);
    const dataFimPrevista = new Date(obra.dataPrevisaoTermino);
    const hoje = new Date();
    
    // Calcular dias decorridos e dias totais
    const diasDecorridos = Math.ceil((hoje - dataInicio) / (1000 * 60 * 60 * 24));
    const diasTotais = Math.ceil((dataFimPrevista - dataInicio) / (1000 * 60 * 60 * 24));
    
    // Projeção de conclusão baseada no progresso atual
    let dataFimProjetada = new Date(dataInicio);
    if (progressoMedio > 0) {
      const diasNecessarios = (diasDecorridos / progressoMedio) * 100;
      dataFimProjetada = new Date(dataInicio.getTime() + (diasNecessarios * 24 * 60 * 60 * 1000));
    }
    
    // Projeção de custo final
    let custoFinalProjetado = 0;
    if (progressoMedio > 0) {
      custoFinalProjetado = (valorTotalMedido / progressoMedio) * 100;
    }
    
    // Desvio de prazo em dias
    const desvioPrazo = Math.ceil((dataFimProjetada - dataFimPrevista) / (1000 * 60 * 60 * 24));
    
    // Desvio de custo
    const desvioCusto = custoFinalProjetado - valorTotalPrevisto;
    
    setProjecoes({
      progressoAtual: progressoMedio,
      valorPrevisto: valorTotalPrevisto,
      valorMedido: valorTotalMedido,
      dataFimPrevista: dataFimPrevista,
      dataFimProjetada: dataFimProjetada,
      desvioPrazo: desvioPrazo,
      desvioCusto: desvioCusto,
      diasDecorridos: diasDecorridos,
      diasTotais: diasTotais,
      custoFinalProjetado: custoFinalProjetado
    });
  }; 
  
  // Calcular medições acumuladas
  const calcularMedicoesAcumuladas = (obra) => {
    if (!obra || !obra.itens || obra.itens.length === 0) {
      setMedicoesAcumuladas([]);
      return;
    }

    // Obter todas as datas de medição
    const todasDatas = [];
    obra.itens.forEach(item => {
      if (item.detalhes) {
        item.detalhes.forEach(detalhe => {
          if (!todasDatas.includes(detalhe.data)) {
            todasDatas.push(detalhe.data);
          }
        });
      }
    });

    // Ordenar datas
    todasDatas.sort();

    // Calcular medições acumuladas por data
    const medicoesAcumuladasPorData = todasDatas.map(data => {
      const dataAtual = new Date(data);
      const medicoesAteData = {};
      
      obra.itens.forEach(item => {
        if (!medicoesAteData[item.id]) {
          medicoesAteData[item.id] = {
            item: item.item,
            descricao: item.descricao,
            unidade: item.unidade,
            quantidade: item.quantidade,
            preco: item.preco,
            total: item.total,
            quantidadeMedida: 0,
            totalMedido: 0,
            progresso: 0
          };
        }
        
        if (item.detalhes) {
          item.detalhes.forEach(detalhe => {
            const dataDetalhe = new Date(detalhe.data);
            if (dataDetalhe <= dataAtual) {
              medicoesAteData[item.id].quantidadeMedida += detalhe.quantidade;
              medicoesAteData[item.id].totalMedido += detalhe.total;
            }
          });
          
          // Calcular progresso
          medicoesAteData[item.id].progresso = (medicoesAteData[item.id].quantidadeMedida / item.quantidade) * 100;
        }
      });
      
      // Calcular totais
      const totalQuantidadeMedida = Object.values(medicoesAteData).reduce((total, item) => total + item.quantidadeMedida, 0);
      const totalValorMedido = Object.values(medicoesAteData).reduce((total, item) => total + item.totalMedido, 0);
      const totalValorPrevisto = Object.values(medicoesAteData).reduce((total, item) => total + item.total, 0);
      const progressoGeral = (totalValorMedido / totalValorPrevisto) * 100;
      
      return {
        data,
        dataFormatada: formatarData(data),
        itens: Object.values(medicoesAteData),
        totalQuantidadeMedida,
        totalValorMedido,
        totalValorPrevisto,
        progressoGeral
      };
    });

    setMedicoesAcumuladas(medicoesAcumuladasPorData);
  };

  // Calcular dados para a Curva S
  const calcularDadosCurvaS = (obra) => {
    if (!obra || !obra.itens || obra.itens.length === 0) {
      setDadosCurvaS([]);
      return;
    }

    // Obter todas as datas de medição
    const todasDatas = [];
    obra.itens.forEach(item => {
      if (item.detalhes) {
        item.detalhes.forEach(detalhe => {
          if (!todasDatas.includes(detalhe.data)) {
            todasDatas.push(detalhe.data);
          }
        });
      }
    });

    // Adicionar data de início e previsão de término
    if (!todasDatas.includes(obra.dataInicio)) {
      todasDatas.unshift(obra.dataInicio);
    }
    if (!todasDatas.includes(obra.dataPrevisaoTermino)) {
      todasDatas.push(obra.dataPrevisaoTermino);
    }

    // Ordenar datas
    todasDatas.sort();

    // Calcular valor total da obra
    const valorTotalObra = obra.itens.reduce((total, item) => total + item.total, 0);

    // Calcular progresso planejado (linear entre início e fim)
    const dadosCurva = todasDatas.map((data, index) => {
      const dataAtual = new Date(data);
      const dataInicio = new Date(obra.dataInicio);
      const dataFim = new Date(obra.dataPrevisaoTermino);
      
      const totalDias = (dataFim - dataInicio) / (1000 * 60 * 60 * 24);
      const diasDecorridos = (dataAtual - dataInicio) / (1000 * 60 * 60 * 24);
      
      // Progresso linear planejado
      let progressoPlanejado = (diasDecorridos / totalDias) * 100;
      progressoPlanejado = Math.min(100, Math.max(0, progressoPlanejado));
      
      // Calcular progresso real
      let valorMedidoAteData = 0;
      obra.itens.forEach(item => {
        if (item.detalhes) {
          item.detalhes.forEach(detalhe => {
            const dataDetalhe = new Date(detalhe.data);
            if (dataDetalhe <= dataAtual && detalhe.operacao === 'Medição') {
              valorMedidoAteData += detalhe.total;
            }
          });
        }
      });
      
      const progressoReal = (valorMedidoAteData / valorTotalObra) * 100;
      
      return {
        data,
        dataFormatada: formatarData(data),
        progressoPlanejado,
        progressoReal: Math.min(100, Math.max(0, progressoReal)),
        valorMedidoAteData,
        diasDecorridos
      };
    });

    setDadosCurvaS(dadosCurva);
  };  
  
  // Exportar relatório para Excel
  const exportarRelatorioParaExcel = () => {
    if (!obra) {
      toast.error('Não há dados para exportar!');
      return;
    }

    // Criar workbook
    const wb = XLSX.utils.book_new();

    // Adicionar planilha de resumo
    const dadosResumo = [
      {
        'Descrição': 'Nome da Obra',
        'Valor': obra.nome
      },
      {
        'Descrição': 'Empreiteiro',
        'Valor': obra.empreiteiro
      },
      {
        'Descrição': 'Valor Contratual',
        'Valor': parseFloat(obra.valor).toFixed(2)
      },
      {
        'Descrição': 'Data de Início',
        'Valor': formatarData(obra.dataInicio)
      },
      {
        'Descrição': 'Previsão de Término',
        'Valor': formatarData(obra.dataPrevisaoTermino)
      }
    ];

    if (projecoes) {
      dadosResumo.push(
        {
          'Descrição': 'Progresso Atual (%)',
          'Valor': projecoes.progressoAtual.toFixed(2)
        },
        {
          'Descrição': 'Valor Medido',
          'Valor': projecoes.valorMedido.toFixed(2)
        },
        {
          'Descrição': 'Projeção de Término',
          'Valor': formatarData(projecoes.dataFimProjetada)
        },
        {
          'Descrição': 'Desvio de Prazo (dias)',
          'Valor': projecoes.desvioPrazo
        },
        {
          'Descrição': 'Custo Final Projetado',
          'Valor': projecoes.custoFinalProjetado.toFixed(2)
        },
        {
          'Descrição': 'Desvio de Custo',
          'Valor': projecoes.desvioCusto.toFixed(2)
        }
      );
    }

    const wsResumo = XLSX.utils.json_to_sheet(dadosResumo);
    XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");

    // Adicionar planilha de acumulados de medição
    if (obra.itens && obra.itens.length > 0) {
      const dadosAcumulados = obra.itens.map(item => {
        const saldoQuantidade = item.quantidade - (item.quantidadeMedida || 0);
        const saldoValor = item.total - (item.totalMedido || 0);
        
        return {
          'Item': item.item,
          'Descrição': item.descricao,
          'Unidade': item.unidade,
          'Quantidade Total': item.quantidade,
          'Preço Unitário': item.preco,
          'Valor Total': item.total,
          'Quantidade Medida': item.quantidadeMedida || 0,
          'Valor Medido': item.totalMedido || 0,
          'Saldo Quantidade': saldoQuantidade,
          'Saldo Valor': saldoValor,
          'Progresso (%)': item.progresso || 0
        };
      });

      const wsAcumulados = XLSX.utils.json_to_sheet(dadosAcumulados);
      XLSX.utils.book_append_sheet(wb, wsAcumulados, "Acumulados de Medição");
    }

    // Adicionar planilha de curva S
    if (dadosCurvaS.length > 0) {
      const dadosCurvaExcel = dadosCurvaS.map(ponto => ({
        'Data': ponto.dataFormatada,
        'Progresso Planejado (%)': ponto.progressoPlanejado.toFixed(2),
        'Progresso Real (%)': ponto.progressoReal.toFixed(2),
        'Valor Medido': ponto.valorMedidoAteData.toFixed(2),
        'Dias Decorridos': ponto.diasDecorridos.toFixed(0)
      }));

      const wsCurvaS = XLSX.utils.json_to_sheet(dadosCurvaExcel);
      XLSX.utils.book_append_sheet(wb, wsCurvaS, "Curva S");
    }

    // Adicionar planilha de alertas
    if (alertas.length > 0) {
      const dadosAlertas = alertas.map(alerta => ({
        'Tipo': alerta.tipo === 'erro' ? 'Crítico' : 'Aviso',
        'Mensagem': alerta.mensagem
      }));

      const wsAlertas = XLSX.utils.json_to_sheet(dadosAlertas);
      XLSX.utils.book_append_sheet(wb, wsAlertas, "Alertas");
    }

    // Salvar o arquivo
    const dataAtual = new Date().toISOString().split('T')[0];
    const nomeArquivo = obra.nome.replace(/[^a-zA-Z0-9]/g, '_');
    XLSX.writeFile(wb, `Relatorio_${nomeArquivo}_${dataAtual}.xlsx`);
    
    toast.success('Relatório exportado para Excel com sucesso!');
  };  
  
  if (!obra) {
    return <div>Carregando dados da obra...</div>;
  }

  // Verificar se a obra tem itens
  const semItens = !obra.itens || obra.itens.length === 0;

  return (
    <div className="relatorio-gerencial">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>Relatório Gerencial da Obra</h3>
        <button 
          className="button" 
          onClick={exportarRelatorioParaExcel}
          style={{ 
            backgroundColor: '#28a745', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            padding: '8px 12px',
            fontSize: '14px'
          }}
        >
          <FileDown size={16} />
          Exportar Relatório
        </button>
      </div>

      {/* Mensagem quando não há itens */}
      {semItens && (
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px', 
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          color: '#666'
        }}>
          <Info size={20} style={{ color: '#0d6efd' }} />
          <div>
            <h4 style={{ margin: '0 0 5px 0', color: '#0d6efd' }}>Obra sem itens cadastrados</h4>
            <p style={{ margin: 0 }}>
              Esta obra ainda não possui itens cadastrados. Adicione itens na seção de medições para visualizar o relatório gerencial completo.
            </p>
          </div>
        </div>
      )}
      
      {/* Informações básicas da obra */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px', 
        padding: '20px', 
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        marginBottom: '30px'
      }}>
        <h4 style={{ marginBottom: '15px' }}>Informações da Obra</h4>
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '15px'
        }}>
          <div>
            <strong>Nome:</strong>
            <div>{obra.nome}</div>
          </div>
          <div>
            <strong>Empreiteiro:</strong>
            <div>{obra.empreiteiro}</div>
          </div>
          <div>
            <strong>Valor Contratual:</strong>
            <div>{formatarMoeda(obra.valor)}</div>
          </div>
          <div>
            <strong>Status:</strong>
            <div>{obra.status}</div>
          </div>
          <div>
            <strong>Data de Início:</strong>
            <div>{formatarData(obra.dataInicio)}</div>
          </div>
          <div>
            <strong>Previsão de Término:</strong>
            <div>{formatarData(obra.dataPrevisaoTermino)}</div>
          </div>
          {obra.contrato && (
            <div>
              <strong>Contrato:</strong>
              <div>{obra.contrato}</div>
            </div>
          )}
          {obra.licitacao && (
            <div>
              <strong>Licitação:</strong>
              <div>{obra.licitacao}</div>
            </div>
          )}
          {obra.processo && (
            <div>
              <strong>Processo:</strong>
              <div>{obra.processo}</div>
            </div>
          )}
        </div>
      </div>
      
      {/* Resumo e Projeções - Só exibir se houver itens */}
      {projecoes && !semItens && (
        <div style={{ marginBottom: '30px' }}>
          <h4 style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TrendingUp size={18} /> Projeções e Resumo
          </h4>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
            gap: '20px',
            marginBottom: '20px'
          }}>
            {/* Card de Progresso */}
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '15px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              border: '1px solid #d1e7dd',
              borderLeft: '5px solid #198754'
            }}>
              <h5 style={{ margin: '0 0 10px 0', color: '#0f5132', fontSize: '16px' }}>Progresso da Obra</h5>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>Progresso Atual:</span>
                <strong>{formatarPercentual(projecoes.progressoAtual)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>Dias Decorridos:</span>
                <strong>{projecoes.diasDecorridos} de {projecoes.diasTotais} dias</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#666' }}>Tempo Decorrido:</span>
                <strong>{formatarPercentual((projecoes.diasDecorridos / projecoes.diasTotais) * 100)}</strong>
              </div>
              
              {/* Barra de Progresso */}
              <div style={{ marginTop: '15px' }}>
                <div style={{ 
                  width: '100%', 
                  height: '10px', 
                  backgroundColor: '#e9ecef',
                  borderRadius: '5px',
                  overflow: 'hidden',
                  marginBottom: '5px'
                }}>
                  <div style={{ 
                    width: `${projecoes.progressoAtual}%`, 
                    height: '100%', 
                    backgroundColor: '#198754',
                    borderRadius: '5px'
                  }}></div>
                </div>
                <div style={{ 
                  width: '100%', 
                  height: '5px', 
                  backgroundColor: '#e9ecef',
                  borderRadius: '5px',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    width: `${(projecoes.diasDecorridos / projecoes.diasTotais) * 100}%`, 
                    height: '100%', 
                    backgroundColor: '#6c757d',
                    borderRadius: '5px'
                  }}></div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  <span>Progresso</span>
                  <span>Tempo</span>
                </div>
              </div>
            </div>
            
            {/* Card de Financeiro */}
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '15px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              border: '1px solid #cfe2ff',
              borderLeft: '5px solid #0d6efd'
            }}>
              <h5 style={{ margin: '0 0 10px 0', color: '#084298', fontSize: '16px' }}>Resumo Financeiro</h5>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>Valor Previsto:</span>
                <strong>{formatarMoeda(projecoes.valorPrevisto)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>Valor Medido:</span>
                <strong>{formatarMoeda(projecoes.valorMedido)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>Valor Contratual:</span>
                <strong>{formatarMoeda(obra.valor)}</strong>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                padding: '8px',
                backgroundColor: projecoes.desvioCusto > 0 ? '#f8d7da' : '#d1e7dd',
                borderRadius: '5px',
                marginTop: '10px'
              }}>
                <span style={{ fontWeight: 'bold' }}>Desvio Projetado:</span>
                <strong style={{ color: projecoes.desvioCusto > 0 ? '#842029' : '#0f5132' }}>
                  {formatarMoeda(projecoes.desvioCusto)}
                </strong>
              </div>
            </div>
            
            {/* Card de Prazos */}
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '15px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              border: '1px solid #fff3cd',
              borderLeft: '5px solid #ffc107'
            }}>
              <h5 style={{ margin: '0 0 10px 0', color: '#664d03', fontSize: '16px' }}>Prazos</h5>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>Data de Início:</span>
                <strong>{formatarData(obra.dataInicio)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>Previsão Original:</span>
                <strong>{formatarData(obra.dataPrevisaoTermino)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>Projeção de Término:</span>
                <strong>{formatarData(projecoes.dataFimProjetada)}</strong>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                padding: '8px',
                backgroundColor: projecoes.desvioPrazo > 0 ? '#f8d7da' : '#d1e7dd',
                borderRadius: '5px',
                marginTop: '10px'
              }}>
                <span style={{ fontWeight: 'bold' }}>Desvio de Prazo:</span>
                <strong style={{ color: projecoes.desvioPrazo > 0 ? '#842029' : '#0f5132' }}>
                  {projecoes.desvioPrazo} dias
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}      

      {/* Alertas - Só exibir se houver alertas */}
      {alertas.length > 0 && !semItens && (
        <div style={{ marginBottom: '30px' }}>
          <h4 style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertTriangle size={18} /> Alertas e Desvios
          </h4>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {alertas.map((alerta, index) => (
              <div key={index} style={{ 
                padding: '12px 15px',
                borderRadius: '8px',
                backgroundColor: alerta.tipo === 'erro' ? '#f8d7da' : '#fff3cd',
                border: `1px solid ${alerta.tipo === 'erro' ? '#f5c2c7' : '#ffecb5'}`,
                color: alerta.tipo === 'erro' ? '#842029' : '#664d03',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                {alerta.icone}
                <span>{alerta.mensagem}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Curva S - Só exibir se houver dados */}
      {dadosCurvaS.length > 0 && !semItens && (
        <div style={{ marginBottom: '30px' }}>
          <h4 style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TrendingUp size={18} /> Curva S - Progresso Planejado vs. Realizado
          </h4>
          
          <div style={{ 
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Data</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Progresso Planejado</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Progresso Real</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Valor Medido</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Diferença</th>
                </tr>
              </thead>
              <tbody>
                {dadosCurvaS.map((ponto, index) => {
                  const diferenca = ponto.progressoReal - ponto.progressoPlanejado;
                  return (
                    <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '10px' }}>{ponto.dataFormatada}</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>{formatarPercentual(ponto.progressoPlanejado)}</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>{formatarPercentual(ponto.progressoReal)}</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>{formatarMoeda(ponto.valorMedidoAteData)}</td>
                      <td style={{ 
                        padding: '10px', 
                        textAlign: 'right',
                        color: diferenca < 0 ? '#842029' : '#0f5132',
                        backgroundColor: diferenca < 0 ? '#f8d7da' : '#d1e7dd',
                        fontWeight: 'bold'
                      }}>
                        {formatarPercentual(diferenca)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelatorioGerencial;